import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { Currency, SNAPSHOT_REPORT_SKU } from '@galaxy/billing';
import { BillingFrequency } from '@vendasta/shared';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { map, startWith, take, tap } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { PricePipe } from '../common/pipes/price.pipe';
import { BillingApiService } from '../core/billing/billing-api.service';
import { PurchaseCost } from '../core/billing/purchase-cost';
import { HidePricingGuard } from '../core/guards/hide-pricing-guard.service';
import { formatDisplayPrice } from '../core/utils/pipe-utils';
import { ManageSubscriptionsService } from '../subscription-tier/utils/manage-subscriptions.service';
import { BillingAction, CheckoutService } from './checkout.service';

interface Upgrade {
  show: boolean;
  cta: string;
  nextTierId: string;
  nextTierLimit: string;
}

interface Pricing {
  price: PurchaseCost;
  pricingString: string;
  showPricing: boolean;
  showContractPricing: boolean;
  upgrade: Upgrade;
}

const INFERABLE_FIELDS = ['website', 'workNumber', 'address'];

@Component({
  templateUrl: './checkout-dialog.component.html',
  styleUrls: ['./checkout-dialog.component.scss'],
  standalone: false,
})
export class CheckoutDialogComponent implements OnInit {
  partnerId: string;
  successLabel: string;
  productSKU: string;
  title: string;
  description: string;
  billingInfoEditable = false;
  createButtonId: string;
  accountGroupId?: string;
  accountType?: string;
  isInferMissingData: FormControl;
  private canCheckout$: Observable<boolean>;

  private showContractPricing$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private showContractPricing$: Observable<boolean> = this.showContractPricing$$.asObservable();
  snapshotActionEnabled$: Observable<boolean>;
  shouldInferAccountData$: Observable<boolean>;

  pricing$: Observable<Pricing>;
  freeSnapshotsRemaining$: Observable<number>;

  billingAction$: Observable<BillingAction>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      partnerId: string;
      successLabel: string;
      productSKU: string;
      title: string;
      description: string;
      createButtonId: string;
      accountGroupId?: string;
      accountType?: string;
      canCheckout$?: Observable<boolean>;
      hideContractPricing?: boolean;
    },
    public dialogRef: MatDialogRef<CheckoutDialogComponent>,
    private billingApiService: BillingApiService,
    private app: AppConfigService,
    private pricePipe: PricePipe,
    private hidePricingGuard: HidePricingGuard,
    private manageSubscriptionsService: ManageSubscriptionsService,
    private accountGroupService: AccountGroupService,
    private snapshotCheckoutService: CheckoutService,
  ) {
    this.partnerId = data.partnerId;
    this.successLabel = data.successLabel;
    this.productSKU = data.productSKU;
    this.title = data.title;
    this.description = data.description;
    this.billingInfoEditable = this.app.config.userCanAccessBilling;
    this.createButtonId = data.createButtonId || data.partnerId + '-createId';
    this.accountGroupId = data.accountGroupId;
    this.accountType = data.accountType || 'account';
    this.canCheckout$ = data.canCheckout$ || of(true);
  }

  ngOnInit(): void {
    this.billingAction$ = this.snapshotCheckoutService.billingAction$;
    this.snapshotCheckoutService.loadProductRequest(this.partnerId, this.productSKU);

    this.isInferMissingData = new FormControl(false);

    if (this.accountGroupId) {
      this.shouldInferAccountData$ = this.accountGroupService
        .get(this.accountGroupId, new ProjectionFilter({ richData: true, napData: true }))
        .pipe(
          take(1),
          map((accountGroup) => {
            const hasNAPFields =
              accountGroup?.napData?.website &&
              accountGroup?.napData?.address &&
              accountGroup?.napData?.workNumber?.some((n) => n);
            const alreadyInferred = INFERABLE_FIELDS.every((field) =>
              accountGroup?.richData?.inferredAttributes?.includes(field),
            );
            return !hasNAPFields && !alreadyInferred;
          }),
        );

      this.shouldInferAccountData$.pipe(
        tap((shouldInfer) => {
          this.isInferMissingData.setValue(shouldInfer);
        }),
      );
    }

    const showPricing$ = this.hidePricingGuard.canActivate().pipe(map((hidePricing) => !hidePricing));

    const price$ = this.getPurchaseCost(this.partnerId, this.productSKU);

    this.snapshotActionEnabled$ = combineLatest([this.canCheckout$, this.billingAction$]).pipe(
      map(([canCheckout, billingAction]) => {
        return (
          canCheckout &&
          (billingAction === BillingAction.PAYMENT_CONFIGURED ||
            billingAction === BillingAction.NO_INSTANT_PAYMENT_NEEDED)
        );
      }),
    );

    const pricingString$ = price$.pipe(map((purchaseCost) => this.getPurchasePriceString(purchaseCost)));

    const upgradeData$ = this.manageSubscriptionsService.cardData$.pipe(
      map((cards) => {
        const card = cards.find((c) => c.skus.some((sku) => sku === this.productSKU));
        if (card === undefined || !card.showCard) {
          return undefined;
        }
        return card;
      }),
    );

    this.freeSnapshotsRemaining$ = upgradeData$.pipe(
      map((data) => {
        const index = data?.skus?.findIndex((c) => c === SNAPSHOT_REPORT_SKU);
        if (index > -1) {
          const ssl = data.subscriptionLimits[index];
          return ssl.limitMax - ssl.currentValue < 0 ? 0 : ssl.limitMax - ssl.currentValue;
        }
        return 0;
      }),
    );

    this.pricing$ = combineLatest([
      price$,
      pricingString$,
      showPricing$,
      this.showContractPricing$,
      upgradeData$,
      this.app.config$,
    ]).pipe(
      map(([price, priceString, showPricing, showContractPricing, upgradeData, appConfig]) => {
        return <Pricing>{
          price: price,
          pricingString: priceString,
          showPricing: showPricing,
          showContractPricing: showContractPricing,
          upgrade: <Upgrade>{
            show:
              upgradeData !== undefined &&
              price?.totalCost > 0 &&
              showContractPricing &&
              (appConfig.userIsAdmin || appConfig.supportEnabled),
            cta: upgradeData?.upgradeCTA,
            nextTierId: upgradeData?.nextTierId,
            nextTierLimit: upgradeData?.nextTierLimit,
          },
        };
      }),
      startWith({
        showPricing: false,
        showContractPricing: false,
        upgrade: {
          show: false,
        },
      } as Pricing),
    );
  }

  getPurchasePriceString(purchaseCost: PurchaseCost): string {
    if (this?.data?.hideContractPricing ?? false) {
      this.showContractPricing$$.next(false);
      return null;
    }

    if (purchaseCost) {
      this.showContractPricing$$.next(true);
      return formatDisplayPrice(
        purchaseCost.totalCost,
        purchaseCost.currency,
        purchaseCost.billingFrequency as BillingFrequency,
      );
    } else {
      this.showContractPricing$$.next(false);
      return null;
    }
  }

  getDiscountDescription(purchaseCost: PurchaseCost): string {
    return 'Discount: ' + this.pricePipe.transform(purchaseCost.discountAmount, Currency[purchaseCost.currency]);
  }

  getPurchaseCost(merchantId: string, SKU: string, quantity = 1): Observable<PurchaseCost> {
    return this.billingApiService.getPurchaseCost(merchantId, SKU, quantity);
  }

  protected async closeSuccess(): Promise<void> {
    const isEnabled = await firstValueFrom(this.snapshotActionEnabled$);
    if (isEnabled) {
      this.dialogRef.close({ status: 'success', inferMissingData: this.isInferMissingData.value });
    }
  }

  loadProductRequest(): void {
    this.snapshotCheckoutService.loadProductRequest(this.partnerId, this.productSKU);
  }
}
